/**
 * <s-related-parts>: swaps the server-rendered "related parts" list for parts
 * that share the product's vehicle tags. data-feeds holds one or more
 * tag-filtered collection URLs (most specific first, e.g. S650 then Mustang),
 * each rendered by templates/collection.related.liquid. Results are merged in
 * order, the current product is skipped, and the server list fills any gaps.
 * If every feed fails or is empty, the server list stays as is.
 */
const LIMIT = 4;

class RelatedParts extends HTMLElement {
  connectedCallback() {
    const feeds = (this.dataset.feeds || '').split('|').filter(Boolean);
    this.list = this.querySelector('[data-related-list]');
    if (!feeds.length || !this.list) return;
    this.controller = new AbortController();
    this.load(feeds).catch(() => {});
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async load(feeds) {
    const current = this.dataset.current;
    const picked = new Map();
    for (const url of feeds) {
      if (picked.size >= LIMIT) break;
      let html;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) continue;
        html = await response.text();
      } catch {
        continue;
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const item of doc.querySelectorAll('[data-related-handle]')) {
        const handle = item.dataset.relatedHandle;
        if (handle === current || picked.has(handle)) continue;
        picked.set(handle, item);
        if (picked.size >= LIMIT) break;
      }
    }
    if (!picked.size) return;

    // Fill remaining slots from the server-rendered category list.
    for (const item of this.list.querySelectorAll('[data-related-handle]')) {
      if (picked.size >= LIMIT) break;
      const handle = item.dataset.relatedHandle;
      if (!picked.has(handle)) picked.set(handle, item);
    }

    const items = [...picked.values()].map((item) => document.importNode(item, true));
    this.list.replaceChildren(...items);

    // Only now that vehicle parts are showing, switch the heading and link.
    const region = this.closest('section') || this.parentElement;
    const heading = region?.querySelector('[data-related-heading]');
    const viewAll = region?.querySelector('[data-related-view-all]');
    if (heading && this.dataset.vehicleHeading) heading.textContent = this.dataset.vehicleHeading;
    if (viewAll && this.dataset.vehicleUrl) viewAll.setAttribute('href', this.dataset.vehicleUrl);
  }
}

if (!customElements.get('s-related-parts')) {
  customElements.define('s-related-parts', RelatedParts);
}
